import { useState, useEffect, useRef } from "react";
import * as styles from "./ProductDetails.module.css";
import { motion } from "framer-motion";
import callIcon from "../../assets/images/call-icon.png";
import stockIdeaIcon from "../../assets/images/idea-icon.png";
import portfolioIcon from "../../assets/images/portfolio-icon.png";
import callIconInactive from "../../assets/images/call-icon-inactive.png";
import stockIdeaIconInactive from "../../assets/images/idea-icon-inactive.png";
import portfolioIconInactive from "../../assets/images/portfolio-icon-inactive.png";
import callImage from "../../assets/webp/call-image.webp";
import stockIdeaImage from "../../assets/images/cimage.png";
import portfolioImage from "../../assets/images/pimage.png";
import callImageMobile from "../../assets/webp/call-image-mobile.webp";
import stockIdeaImageMobile from "../../assets/webp/stock-idea-image-mobile.webp";
import portfolioImageMobile from "../../assets/images/portfolio-image-mobile.png";

const slides = [
  {
    id: 1,
    title: "Connect with experts",
    icon: callIcon,
    inactiveIcon: callIconInactive,
    image: callImage,
    mobileImage: callImageMobile,
  },
  {
    id: 2,
    title: "Stock Ideas",
    icon: stockIdeaIcon,
    inactiveIcon: stockIdeaIconInactive,
    image: stockIdeaImage,
    mobileImage: stockIdeaImageMobile,
  },
  {
    id: 3,
    title: "Automated Portfolio",
    icon: portfolioIcon,
    inactiveIcon: portfolioIconInactive,
    image: portfolioImage,
    mobileImage: portfolioImageMobile,
  },
];

const TIMER_DURATION = 5000;
const RADIUS = 19;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const ProductDetails = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const [progress, setProgress] = useState(100);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev <= 0) {
          setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
          return 100;
        }
        return prev - 2;
      });
    }, 100);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth <= 600;
      setIsMobile(mobileView);
    };
  
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleClick = (index: number) => {
    setActiveIndex(index);
    setProgress(100);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;

    if (touchStartX.current !== null && touchEndX.current !== null) {
      const swipeDistance = touchStartX.current - touchEndX.current;

      if (swipeDistance > 50) {
        setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length);
        setProgress(100);
      } else if (swipeDistance < -50) {
        setActiveIndex((prevIndex) =>
          prevIndex === 0 ? slides.length - 1 : prevIndex - 1
        );
        setProgress(100);
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div
      className={`${styles.productDetailsContainer} pt-100`}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.carouselContainer}>
        <div className={styles.carouselButtons}>
          {slides.map((slide, index) => (
            <div key={slide.id} className={styles.buttonWrapper}>
              <div
                className={`${styles.carouselButton} ${
                  index === activeIndex ? styles.active : ""
                }`}
                onClick={() => handleClick(index)}
              >
                <img
                  src={index === activeIndex ? slide.icon : slide.inactiveIcon}
                  alt={slide.title}
                  className={styles.icon}
                />
                <svg className={styles.progressCircle} viewBox="0 0 40 40">
                  {index === activeIndex && (
                    <motion.circle
                      className={styles.progressBar}
                      cx="20"
                      cy="20"
                      r={RADIUS}
                      strokeWidth="2"
                      strokeDasharray={CIRCUMFERENCE}
                      strokeDashoffset={(1 - progress / 100) * CIRCUMFERENCE}
                      animate={{
                        strokeDashoffset: (1 - progress / 100) * CIRCUMFERENCE,
                      }}
                      transition={{ ease: "linear", duration: 0.1 }}
                    />
                  )}
                </svg>
              </div>
              <span className={styles.carouselButtonName}>{slide.title}</span>
            </div>
          ))}
        </div>
        <div className={styles.carouselContent}>
          {slides.map((slide, index) => (
            <img
              key={slide.id}
              src={isMobile ? slide.mobileImage : slide.image}
              alt={slide.title}
              className={`${styles.carouselImage} ${
                index === activeIndex ? styles.activeImage : ""
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;